'use client';

/** @jsxImportSource react */

import type { FirstTouch } from '@OnArk/utils/attribution';

import Providers from '../../providers';
import SignupTrialForm from '../../start/_components/SignupTrialForm';

/**
 * The real card-on-trial signup form, embedded in a marketing landing page.
 *
 * `/free-trial` and `/welcome-aboard-trial` used to hand every CTA off to
 * `/sign-up`. That's a click and a page load between an ad visitor and the
 * only thing these pages exist to get — so the form now lives on the page and
 * the landing IS the signup.
 *
 * ⚠️ This mounts `Providers` (tRPC + react-query) itself. `app/(marketing)/
 * layout.tsx` is deliberately provider-free so the other ~30 marketing pages
 * don't ship the client graph — the mount belongs on the two pages that
 * actually need it, exactly like `app/sign-up/layout.tsx` does for its segment.
 * Locked by `__tests__/product-providers-segment.test.ts`.
 */
export function SignupTrialEmbed({
  emitFunnelEntry = true,
  formHeading = 'Start your free trial',
  formMeta = 'Free 21-day trial · $0 today · Cancel anytime before day 21',
  landing,
  offer,
  seedAttribution,
}: {
  /**
   * Only ONE embed per page may claim funnel entry. These pages carry two (a
   * hero form and a closing form); a second `signup_trial.started` per
   * pageview would halve every landing→conversion rate computed off it.
   */
  emitFunnelEntry?: boolean;
  formHeading?: string;
  formMeta?: string;
  /** Funnel label for `signup_trial.*` — the page, not the URL tail. */
  landing: string;
  /** When 'welcome-aboard', route to the WA checkout after onboarding. */
  offer?: string;
  /**
   * First-touch defaults seeded on mount. /start/pco needs this: visitors from
   * the Planning Center integration directory arrive with no utm_*, and without
   * a seed they record as Direct. A real utm_source in the URL still wins.
   */
  seedAttribution?: FirstTouch;
}) {
  return (
    <Providers>
      <div className="w-full">
        <SignupTrialForm
          className="rounded-[20px] border border-[color:var(--color-border-soft)] bg-white p-6 shadow-[0_18px_48px_-16px_rgba(15,11,26,0.18)] sm:p-7"
          emitFunnelEntry={emitFunnelEntry}
          formHeading={formHeading}
          formMeta={formMeta}
          landing={landing}
          offer={offer}
          seedAttribution={seedAttribution}
        />
      </div>
    </Providers>
  );
}
